import { defaultDataIdFromObject, InMemoryCache } from '@apollo/client';
import { mergePagedResults } from '../paged-results';
import { DateTime } from 'luxon';
import {
    GQLCalendarEventsPage,
    GQLExternalCalendarEventGroup,
    GQLSearchContactsResponse,
} from '@upheal/graphql/definitions/schema.generated';

/**
 * @description
 * There is a period of time after event is created when Nylas doesn't return it in the list of events. (Nylas v2 API)
 * Therefore when fetching list of events this event is not yet there, so the client cache would remove it from the list.
 * This is super confusing for customers.
 * To prevent this we keep a list of newly added events and we keep them in the list for 30 seconds.
 */
export const newlyAddedCalendarEventsCache = {
    items: new Set<string>(),
    addItem(id: string) {
        this.items.add(id);
        setTimeout(() => {
            this.items.delete(id);
        }, 30_000);
    },
};

export const cache = new InMemoryCache({
    dataIdFromObject: (object) => {
        switch (object.__typename) {
            case 'ProviderUserPreferences':
                return `ProviderUserPreferences:${object.providerUserId}`;
            case 'PlanSubscription':
                // PlanSubscription has ID, but we want to use 'organizationID'
                // in the cache so the parent objects like CustomerDetails
                // are updated correctly in situation when plan is changed,
                // e.g.switched from paid to free.
                return `PlanSubscription:${object.organizationId}`;
            case 'ConnectedAccount':
                return `ConnectedAccount:SINGLETON`;
            case 'StripeCustomerAccount':
                return `StripeCustomerAccount:${object.contractId}`;
            default:
                return defaultDataIdFromObject(object);
        }
    },
    typePolicies: {
        EditorSegment: {
            keyFields: false,
        },
        ParagraphFormStaticContent: {
            keyFields: false,
        },
        FormPacket: {
            fields: {
                forms: {
                    merge: false,
                },
            },
        },
        CustomerDetails: {
            // CustomerDetails has no ID, but is the same object even if fetched
            // with different shapes -> this instructs to merge it in the cache.
            merge: true,
        },
        Calendar: {
            // this has to be here because Calendar has no ID
            merge(existing, incoming) {
                return { ...existing, ...incoming };
            },
        },
        ImportProcess: {
            fields: {
                step: {
                    merge: false,
                },
            },
        },
        Query: {
            fields: {
                /**
                 * All calendar events are now stored in one cache,
                 * which simplifies optimistic updates.
                 *
                 * By merging results, users will always see cached data
                 * when navigating between months, ensuring a smoother and faster experience.
                 *
                 * Cache keys include providerUserId to separate events by provider.
                 * This enables OrgAdmins to view multiple provider calendars without cache collisions.
                 */
                calendarScheduleByDates: {
                    keyArgs: ['input', ['teamProviderUserIds']],
                    merge(
                        existing: { calendars: any[] } | undefined,
                        incoming: { calendars: any[] },
                        { readField, canRead },
                    ) {
                        const existingCalendars = existing?.calendars ?? [];
                        const incomingCalendars = incoming.calendars;

                        const existingByProvider = new Map<string, any>();
                        for (const cal of existingCalendars) {
                            const id = readField<string>('providerUserId', cal);
                            if (id) existingByProvider.set(id, cal);
                        }

                        const mergeUniqueById = (
                            existing: readonly any[],
                            incoming: readonly any[],
                        ) => {
                            const uniqueMap = new Map<string, any>();
                            for (const ref of [...existing, ...incoming]) {
                                if (!canRead(ref)) continue;
                                const id = readField<string>('id', ref);
                                if (id) uniqueMap.set(id, ref);
                            }
                            return Array.from(uniqueMap.values());
                        };

                        const mergedCalendars = incomingCalendars.map(
                            (incomingCal) => {
                                const providerUserId = readField<string>(
                                    'providerUserId',
                                    incomingCal,
                                );
                                const existingCal = providerUserId
                                    ? existingByProvider.get(providerUserId)
                                    : undefined;

                                if (!existingCal) return incomingCal;

                                return {
                                    ...incomingCal,
                                    scheduleItems: mergeUniqueById(
                                        readField<any[]>(
                                            'scheduleItems',
                                            existingCal,
                                        ) ?? [],
                                        readField<any[]>(
                                            'scheduleItems',
                                            incomingCal,
                                        ) ?? [],
                                    ),
                                    timeOffEvents: mergeUniqueById(
                                        readField<any[]>(
                                            'timeOffEvents',
                                            existingCal,
                                        ) ?? [],
                                        readField<any[]>(
                                            'timeOffEvents',
                                            incomingCal,
                                        ) ?? [],
                                    ),
                                };
                            },
                        );

                        return { ...incoming, calendars: mergedCalendars };
                    },
                },
                /**
                 * For external calendars, each provider (e.g., Google or iCal) has its own cache.
                 * Since events for external calendars are queried separately per provider,
                 * merging them is not possible—the events are always overwritten.
                 *
                 * This is due to the backend generating event IDs dynamically during each refetch,
                 * and some iCal providers do not provide stable IDs. The events would then multiplicate on refetch.
                 */
                externalCalendarEventsByDates: {
                    keyArgs: (args) => {
                        if (!args?.input?.calendarIds) return 'Other';

                        return args.input.calendarIds[0].provider;
                    },
                    merge(
                        existing: Array<GQLExternalCalendarEventGroup> = [],
                        incoming: Array<GQLExternalCalendarEventGroup>,
                    ): Array<GQLExternalCalendarEventGroup> {
                        const mergedMap = new Map<
                            string,
                            GQLExternalCalendarEventGroup
                        >(existing.map((group) => [group.calendarId, group]));

                        incoming.forEach((group) => {
                            mergedMap.set(group.calendarId, group);
                        });

                        return Array.from(mergedMap.values());
                    },
                },
                calendarEvents: {
                    keyArgs: ['clientUserId'],
                    merge(
                        existing: GQLCalendarEventsPage | undefined,
                        incoming: GQLCalendarEventsPage,
                        { readField, canRead, args },
                    ): GQLCalendarEventsPage {
                        const mergedPagedResults = mergePagedResults(
                            existing && {
                                ...existing,
                                items: existing.items.filter(canRead),
                            },
                            {
                                ...incoming,
                                // let's overwrite pagesize because server doesn't return correct value right now
                                pageSize: args?.pageSize ?? incoming.pageSize,
                                offset: incoming.offset ?? undefined,
                            },
                            {
                                removeMissing: incoming.offset === 0,
                                isItemProtected: (item) => {
                                    const id = readField<string>('id', item);
                                    return Boolean(
                                        id &&
                                            newlyAddedCalendarEventsCache.items.has(
                                                id,
                                            ),
                                    );
                                },
                                equals: (a, b) =>
                                    readField('id', a) === readField('id', b),
                                sort: (a, b) =>
                                    (readField<DateTime>(
                                        'start',
                                        a,
                                    )?.toMillis() ?? 0) -
                                    (readField<DateTime>(
                                        'start',
                                        b,
                                    )?.toMillis() ?? 0),
                            },
                        );

                        return {
                            items: mergedPagedResults.items,
                            hasMore:
                                !existing ||
                                mergedPagedResults.items.length >
                                    existing.items.length
                                    ? incoming.hasMore
                                    : existing.hasMore,
                            pageSize: incoming.pageSize,
                            offset: incoming.offset,
                            nextPageCursor: incoming.nextPageCursor,
                        };
                    },
                },
                availabilityTimeSlots: {
                    keyArgs: [],
                    merge(_, incoming) {
                        return incoming;
                    },
                },
                listAppointmentTypes: {
                    // Ignore only timezoneId; any future args will affect the cache key
                    keyArgs: (args) => {
                        if (!args) return [];
                        return Object.keys(args).filter(
                            (k) => k !== 'timezoneId',
                        );
                    },
                    merge(_, incoming) {
                        return incoming;
                    },
                },
                contactsV2: {
                    keyArgs: ['searchQuery', 'sortBy', 'sortAscending'],
                    merge(
                        existing: GQLSearchContactsResponse | undefined,
                        incoming: GQLSearchContactsResponse,
                        { readField, canRead, args },
                    ): GQLSearchContactsResponse {
                        const mergedPagedResults = mergePagedResults(
                            existing && {
                                ...existing,
                                items: existing.items.filter(canRead),
                            },
                            {
                                items: incoming.items,
                                totalCount: incoming.totalCount,
                                pageSize: args?.pageSize,
                                offset: args?.offset,
                            },
                            {
                                equals: (a, b) =>
                                    readField('id', a) === readField('id', b),
                            },
                        );

                        return {
                            items: mergedPagedResults.items,
                            totalCount: incoming.totalCount,
                        };
                    },
                },
            },
        },
    },
});

// @ts-ignore
window._apolloCache = cache;
